import React, { useContext, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { fetchPsfServiceDssatAllYears, fetchPsfServiceDssatMonths } from "../psfSalesServiceKpi";
import { FilterContext } from "../../../context/Filter";

// Register Chart.js components + datalabels
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const PsfServiceDssatGraph = () => {
  const [psfDssatData, setPsfDssatData] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [loading, setLoading] = useState(true);

  const { arn: parArn, childArn: rawChildArn } = useContext(FilterContext);
  const childArn = rawChildArn || "";

  // Fetch all years for dropdown and default chart
  const fetchAllYearsData = async () => {
    setLoading(true);
    try {
      const response = await fetchPsfServiceDssatAllYears(parArn, childArn);
      setYears(response.map((item) => item.key));
      if (!selectedYear) {
        setPsfDssatData(response);
      }
    } catch (error) {
      console.error("Error fetching all years DSSAT data:", error);
      setPsfDssatData([]);
      setYears([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly data for selected year
  const fetchMonthData = async (year) => {
    setLoading(true);
    try {
      const response = await fetchPsfServiceDssatMonths(parArn, childArn, year);
      setPsfDssatData(response);
    } catch (error) {
      console.error("Error fetching monthly DSSAT data:", error);
      setPsfDssatData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchAllYearsData();
  }, []);

  // Fetch month data when selectedYear changes
  useEffect(() => {
    if (selectedYear) {
      fetchMonthData(selectedYear);
    } else {
      fetchAllYearsData();
    }
  }, [selectedYear]);

  // ✅ Handle Loading State
  if (loading) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  // ✅ Handle No Data State
  if (!psfDssatData || psfDssatData.length === 0) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center text-gray-500 font-medium">
        No Data Available
      </div>
    );
  }

  const chartData = {
    labels: psfDssatData.map((item) => item.key),
    datasets: [
      {
        label: "PSF DSSAT Score",
        data: psfDssatData.map((item) => item.dssat),
        backgroundColor: "#5b9bd5",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 30 } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: {
        anchor: "end",
        align: "end",
        clip: false,
        formatter: (value) => `${value}%`,
        font: { weight: "bold", size: 14 },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { display: false, beginAtZero: true, max: 100 },
    },
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4">
      {/* Year Dropdown */}
      <select
        value={selectedYear}
        onChange={(e) => setSelectedYear(e.target.value)}
        className="border rounded p-2 mb-4"
      >
        <option value="">All Years</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>

      {/* Bar Chart */}
      <div className="w-full max-w-md" style={{ height: "250px" }}>
        <Bar data={chartData} options={chartOptions} height={250} />
      </div>
    </div>
  );
};

export default PsfServiceDssatGraph;
